import React from 'react';
import { Link } from 'react-router-dom';
import { FileCheck, GraduationCap, Users, TrendingUp, CheckCircle2 } from 'lucide-react';

const BusinessModelPage = () => {
  const steps = [
    {
      icon: FileCheck,
      title: "Step 1 — Get Licensed",
      description: "If you are not already licensed, we guide you through obtaining your state life insurance license.",
      details: ["Pre-licensing course materials", "Exam preparation support", "State application assistance"]
    },
    {
      icon: GraduationCap,
      title: "Step 2 — Learn the Business",
      description: "Advisors receive comprehensive training covering all aspects of the business.",
      details: ["Life insurance fundamentals", "Financial protection planning", "Client consultation process", "Compliance and regulations"]
    },
    {
      icon: Users,
      title: "Step 3 — Serve Clients",
      description: "Advisors help families compare insurance solutions and secure coverage.",
      details: ["Term Life Insurance", "Whole Life Insurance", "Universal Life Insurance", "Premium Financing (advanced)"]
    },
    {
      icon: TrendingUp,
      title: "Step 4 — Grow Your Practice",
      description: "Advisors build long-term income through multiple channels.",
      details: ["Serving clients directly", "Building referral networks", "Mentoring new advisors", "Leadership advancement"]
    }
  ];

  return (
    <div className="bg-[#020617] min-h-screen pt-20" data-testid="business-model-page">
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <Link to="/for-advisors" className="text-[#C5A059] text-sm uppercase tracking-widest mb-4 inline-block hover:text-[#D4AF37]">
              ← Back to For Advisors
            </Link>
            <h1 className="font-serif text-4xl md:text-5xl text-white leading-tight mb-6">
              The ShopCompareInsure Advisor Model
            </h1>
            <p className="text-slate-300 text-xl">
              Our platform enables advisors to build a financial services practice helping clients protect their financial future.
            </p>
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="space-y-16">
            {steps.map((step, idx) => {
              const Icon = step.icon;
              return (
                <div key={idx} className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                  <div className="lg:col-span-4">
                    <div className="flex items-center space-x-4 mb-4">
                      <div className="w-14 h-14 rounded-lg bg-[#C5A059]/10 flex items-center justify-center">
                        <Icon className="w-7 h-7 text-[#C5A059]" />
                      </div>
                      <h3 className="font-serif text-2xl text-white">{step.title}</h3>
                    </div>
                    <p className="text-slate-400">{step.description}</p>
                  </div>
                  <div className="lg:col-span-8">
                    <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg">
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {step.details.map((detail, i) => (
                          <li key={i} className="flex items-center space-x-2">
                            <CheckCircle2 className="w-4 h-4 text-[#C5A059] flex-shrink-0" />
                            <span className="text-slate-300 text-sm">{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-serif text-3xl text-white mb-6">Ready to Get Started?</h2>
          <p className="text-slate-400 mb-8 max-w-2xl mx-auto">
            Join our advisor network and start building your financial services career.
          </p>
          <Link
            to="/become-an-agent"
            className="inline-flex items-center justify-center bg-[#C5A059] text-[#020617] hover:bg-[#D4AF37] font-semibold px-8 py-4 uppercase tracking-wider text-sm transition-all"
          >
            Apply Now
          </Link>
        </div>
      </section>
    </div>
  );
};

export default BusinessModelPage;
